import type API from "@/Api/API";
import type { User } from "@/types/user";

export interface TokenPair {
	access_token: string;
	refresh_token?: string;
	expires_in?: number;
}

export default class Auth {
	api: API;

	constructor(api: API) {
		this.api = api;
	}

	async login(email: string, password: string): Promise<TokenPair> {
		return this.api
			.processRequest("POST", "/auth/login", {
				data: {
					email,
					password,
				},
				skipAuth: true,
			})
			.then((res) => res.data);
	}

	// Bypasses processRequest: this must not carry a bearer token, and must not
	// go through the 401 interceptor that calls it.
	async refresh(refreshToken: string): Promise<TokenPair> {
		return this.api.refreshSession(refreshToken);
	}

	async logout(refreshToken: string): Promise<void> {
		return this.api.revokeSession(refreshToken);
	}

	async register(
		email: string,
		firstname: string,
		lastname: string
	): Promise<User> {
		return this.api
			.processRequest("POST", "/auth/register", {
				data: {
					email,
					firstname,
					lastname,
					groups: [],
				},
				skipAuth: true,
			})
			.then((res) => res.data);
	}

	async loginAsGroup(uuid: string): Promise<TokenPair> {
		return this.api
			.processRequest("POST", "/auth/sign_in_as_group", {
				data: {
					group_uuid: uuid,
				},
			})
			.then((res) => res.data);
	}

	async loginBackAsUser(): Promise<TokenPair> {
		return this.api
			.processRequest("POST", "/auth/sign_in_back_as_user")
			.then((res) => res.data);
	}

	async loginAsUser(userUuid: string): Promise<TokenPair> {
		return this.api
			.processRequest("POST", "/auth/sign_in_as_user", {
				data: {
					user_uuid: userUuid,
				},
			})
			.then((res) => res.data);
	}

	async loginBackAsAdmin(): Promise<TokenPair> {
		return this.api
			.processRequest("POST", "/auth/sign_in_back_as_admin")
			.then((res) => res.data);
	}

	async getProfile(): Promise<User> {
		return this.api
			.processRequest("GET", "/auth/profile")
			.then((res) => res.data);
	}

	async confirmAccount(uuid: string, password: string): Promise<void> {
		return this.api
			.processRequest("POST", "/auth/confirm_account", {
				data: {
					uuid,
					password,
				},
				skipAuth: true,
			})
			.then((res) => res.data);
	}

	async resetPassword(token: string, password: string): Promise<void> {
		return this.api
			.processRequest("POST", "/auth/reset_password", {
				data: {
					token,
					password,
				},
				skipAuth: true,
			})
			.then((res) => res.data);
	}

	async forgotPassword(email: string): Promise<void> {
		return this.api
			.processRequest("POST", "/auth/forgot_password", {
				data: {
					email,
				},
				skipAuth: true,
			})
			.then((res) => res.data);
	}
}
